import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth, getRoles } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { cn } from "@/lib/utils";
import { Menu, ArrowLeft } from "lucide-react";
import UserMenu from "./UserMenu";
import LanguageToggle from "./LanguageToggle";
import BrandLogo from "@/components/common/BrandLogo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export function PortalNavItem({ to, label, icon: Icon, end, testId }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors",
          isActive ? "bg-white text-ink-900 shadow-card" : "text-ink-500 hover:text-ink-900 hover:bg-white/60"
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function PortalSidebar({ title, subtitle, items, testPrefix }) {
  const t = useT();
  return (
    <div className="flex flex-col h-full px-4 py-6">
      <div className="flex items-center gap-2 px-2 mb-8">
        <BrandLogo size="lg" />
        <div className="leading-tight min-w-0">
          <div className="font-heading font-semibold text-ink-900 tracking-tight truncate">{title}</div>
          <div className="text-[11px] text-ink-500 truncate max-w-[160px]">{subtitle}</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto -mr-2 pr-2 space-y-1" data-testid={`${testPrefix}-nav`}>
        {items.map((it) => (
          <PortalNavItem key={it.to} to={it.to} end={it.end} label={it.label} icon={it.icon} testId={`${testPrefix}-nav-${it.key}`} />
        ))}
      </nav>
      <div className="pt-4 border-t border-border">
        <NavLink to="/app" data-testid={`${testPrefix}-back-to-app`}
          className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-ink-500 hover:text-ink-900 hover:bg-white/60">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> {t("nav.home")}
        </NavLink>
      </div>
    </div>
  );
}

/** Generic professional portal frame. `allow(roles)` decides access; `items(ctx)` builds the nav. */
export default function PortalShell({ title, subtitle, items, allow, testPrefix, headerLabel }) {
  const { user, platform, b2b, initializing } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const roles = getRoles(user, platform, b2b);
  const allowed = allow(roles);
  const orgId = b2b?.principal?.org_id || platform?.organization?.id || platform?.organization?.org_id || platform?.organization_id || null;
  const orgName = b2b?.principal?.org_name || platform?.organization?.name || platform?.organization_name || "";
  const ctx = { roles, platform, b2b, orgId, orgName };
  const navItems = items(ctx);

  useEffect(() => {
    if (initializing) return;
    if (!user) nav("/login", { replace: true, state: { from: loc.pathname } });
    else if (!allowed) nav("/app", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializing, user, allowed]);

  if (initializing) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <div className="animate-pulse text-ink-500 font-heading text-lg tracking-tight">Inner Compass</div>
      </div>
    );
  }
  if (!allowed) return null;

  const sidebar = <PortalSidebar title={title} subtitle={subtitle(ctx)} items={navItems} testPrefix={testPrefix} />;
  return (
    <div className="min-h-screen flex bg-cream-50">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-border bg-cream-50/80 backdrop-blur">{sidebar}</aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 flex items-center gap-3 h-16 px-4 md:px-6 border-b border-border bg-cream-50/70 backdrop-blur-md" data-testid={`${testPrefix}-topbar`}>
          <div className="flex items-center gap-3 lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="h-10 w-10 rounded-xl border border-border bg-white flex items-center justify-center" aria-label="menu" data-testid={`${testPrefix}-mobile-menu`}>
                  <Menu className="h-5 w-5 text-ink-700" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-80 max-w-[85vw] bg-cream-50">
                <div onClick={() => setMobileOpen(false)}>{sidebar}</div>
              </SheetContent>
            </Sheet>
            <div className="flex items-center gap-2">
              <BrandLogo size="sm" />
              <span className="font-heading font-semibold text-ink-900">{headerLabel || title}</span>
            </div>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <UserMenu />
          </div>
        </header>
        <main className="flex-1 min-w-0 p-4 md:p-8 overflow-x-hidden" data-testid={`${testPrefix}-main`}>
          <div className="mx-auto w-full max-w-7xl animate-fade-up">
            <Outlet context={ctx} />
          </div>
        </main>
      </div>
    </div>
  );
}
